import React from 'react';
import { Link } from 'react-router-dom';
import { ENGAGEMENT, ENGAGEMENT_MULTIPLE, PROOF_STRIP, type Metric } from './siteFacts';

/**
 * The evidence, in one row, from siteFacts so it matches the deck.
 * Each figure carries its definition as a tooltip and an accessible
 * description — a number without one is how the old 35–45% got onto the site.
 */
interface ProofStripProps {
  /** Which PROOF_STRIP labels to show; defaults to the first four. */
  labels?: string[];
  showEngagement?: boolean;
  tone?: 'light' | 'dark';
  /** Replace the default set outright (still capped at six). */
  metrics?: Metric[];
}

const Stat: React.FC<{ m: Metric; dark: boolean }> = ({ m, dark }) => (
  <div title={m.definition}>
    <div className={`text-3xl md:text-4xl font-bold text-ink`}>{m.value}</div>
    <div className={`text-sm mt-1 text-ink-500`}>{m.label}</div>
    <span className="sr-only">{m.definition}</span>
  </div>
);

const ProofStrip: React.FC<ProofStripProps> = ({ labels, showEngagement = true, tone = 'light', metrics }) => {
  const dark = tone === 'dark';
  const picked = labels ? PROOF_STRIP.filter((m) => labels.includes(m.label)) : PROOF_STRIP.slice(0, 4);
  const stats = metrics ?? (showEngagement ? [ENGAGEMENT, ENGAGEMENT_MULTIPLE, ...picked] : picked);

  return (
    <section className={dark ? 'bg-paper-2 border-y border-v2border py-12' : 'bg-white dark:bg-gray-950 py-12 border-y border-gray-100 dark:border-gray-800'}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center">
          {stats.slice(0, 6).map((m) => (
            <Stat key={m.label} m={m} dark={dark} />
          ))}
        </div>
        <p className={`text-xs text-center mt-6 ${dark ? 'text-ink-400' : 'text-gray-500 dark:text-gray-400'}`}>
          Medi-Cal outreach across four CBO federations, measured against published benchmarks (KFF, DHCS, CMS, CHCS, Pew).{' '}
          <Link to="/impact" className="underline">How each number is measured</Link>
        </p>
      </div>
    </section>
  );
};

export default ProofStrip;
